import { SiteItemData, SiteVideoData } from "../../../../interfaces";
import { disablePageRefresh, enablePageRefresh } from "../../../../siteWidgets/siteWidgetUtils";
import { categoryObj } from "./newYnetTvVerticalDynamicComponenta";
import { NewYnetTvVerticalItem } from "./newYnetTvVerticalItem";
import { PromoCarouselLtr } from "../../../newYnetLTRTvVerticalDynamic/components/promoCarouselLtr";
import { VerticalVideoOverlayPlayer } from "../../../verticalVideoOverlayPlayer/verticalVideoOverlayPlayer";
import { PromoCarousel } from "./promoCarousel";
import { isWcmEditor } from "../../../../../config/serverConfig";

interface NewYnetTvVerticalDynamicDesktopProps {
    videoList: SiteItemData[]
    isTabVisible: boolean
    isTitleVisible: boolean
    isSubTitleVisible: boolean
    tabTitleText: string
    repeatAds: number
    maxAds: number
    firstAdPlace: number
    category: categoryObj
    domain: string
    showPrerollAds: boolean
    youtubeLink: string
    whatsappLink: string
    instagramLink: string
    twitterLink: string
    tiktokLink: string
    facebookLink: string
    tabBackgroundColor: string
    isLTR: boolean
    languageCode: string
}

interface NewYnetTvVerticalDynamicDesktopState {
    isOverlayOpen: boolean
    selectedVideo: number
    isPromoCarouselInView: boolean
    currentPromoIndex: number
    lastNonHoveredPromoIndex: number | null
}

export class NewYnetTvVerticalDynamicDesktop extends React.Component<NewYnetTvVerticalDynamicDesktopProps, NewYnetTvVerticalDynamicDesktopState> {
    constructor(props) {
        super(props);
        this.state = {
            selectedVideo: -1,
            currentPromoIndex: 0,
            isOverlayOpen: false,
            isPromoCarouselInView: false,
            lastNonHoveredPromoIndex: null
        };
    }

    private videoGalleryRef;
    private intersectionObserver: IntersectionObserver;

    componentDidMount() {
        if (isWcmEditor()) return

        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    this.setState({ isPromoCarouselInView: true });
                } else {
                    this.setState({ isPromoCarouselInView: false });
                }
            },
            { threshold: 0.5 }
        );

        if (this.videoGalleryRef) {
            this.intersectionObserver.observe(this.videoGalleryRef);
        }
    }

    componentWillUnmount() {
        if (this.intersectionObserver && this.videoGalleryRef) {
            this.intersectionObserver.disconnect();
        }
    }

    private onPromoVideoEnded = () => {
        this.setState((prevState) => {
            const { videoList } = this.props;
            const nextPromoIndex = prevState.currentPromoIndex + 1;

            if (nextPromoIndex < videoList.length) {
                let nextPromoIndexToCheck = nextPromoIndex;

                while (nextPromoIndexToCheck < videoList.length) {
                    if ((videoList[nextPromoIndexToCheck].media as SiteVideoData).promoPath) {
                        return { currentPromoIndex: nextPromoIndexToCheck };
                    }
                    nextPromoIndexToCheck++;
                }
            } else {
                return { currentPromoIndex: -1 }; // once all promos are played, stop the auto playing carousel
            }
        });
    };

    private onOpenVideoGalleryView = (originalVideoListIndex: number) => {
        const { videoList } = this.props;
        const initialFeedSlide = this.getListWithAds().findIndex((element) => element.itemId === videoList[originalVideoListIndex].itemId);
        this.setState({ selectedVideo: initialFeedSlide, currentPromoIndex: 0, isPromoCarouselInView: false }, () => { this.setState({ isOverlayOpen: true }) });
        disablePageRefresh();
    }

    private onCloseVideoGalleryView = () => {
        this.setState({ isOverlayOpen: false, isPromoCarouselInView: true, selectedVideo:-1 });
        enablePageRefresh();
    }

    private onPromoMouseHover = (hoveredIndex: number | null, videoRef?: HTMLVideoElement) => {
        const { currentPromoIndex, lastNonHoveredPromoIndex } = this.state;

        this.setState((prevState) => {
            if (hoveredIndex !== null) {
                return {
                    lastNonHoveredPromoIndex: prevState.currentPromoIndex,
                    currentPromoIndex: hoveredIndex
                };
            } else {
                if (currentPromoIndex !== lastNonHoveredPromoIndex) videoRef && videoRef.pause();

                return {
                    currentPromoIndex: prevState.lastNonHoveredPromoIndex
                };
            }
        });
    }

    private pushGA4Event = (action: string, isOverlayOpen: boolean) => {
        if (typeof window == "undefined" || !window.dataLayer) return;
        const { category } = this.props;
        const actionPrefix = category && category.name ? (category.name === "דף הבית") ? "homepage" : category.name : "homepage";
        const gaAction = isOverlayOpen ? `video_${action}` : `${actionPrefix}_${action}`;

        window.dataLayer.push({
            event: "content_click",
            vertical_video_action: gaAction,
        });
    };

    private getListWithAds = () => {
        const { repeatAds, maxAds, firstAdPlace, videoList, showPrerollAds } = this.props;
        const videoListCopy = [...videoList];
        const videoListLength = videoListCopy.length;
        let numberOfAds = firstAdPlace > videoListLength ? 0 : Math.min(maxAds, videoListLength - firstAdPlace, Math.floor((videoListLength - firstAdPlace) / repeatAds + 1));
        const newListLength = videoListLength + numberOfAds;
        const baseId = "ads.vertical.native.";
        let newList = [];
        let adCount = 1;

        if (showPrerollAds) return this.getListWithPreRollsAds();
        if (maxAds === 0) return videoListCopy;

        for (let i = 0; i < newListLength; i++) {
            if (i < firstAdPlace) {
                newList.push(videoListCopy.shift());
            } else if (i === firstAdPlace && numberOfAds > 0) {
                newList.push(`${baseId}${adCount}`);
                numberOfAds--;
                adCount++;
            } else if (numberOfAds > 0 && (i - firstAdPlace) % (repeatAds + 1) === 0) {
                newList.push(`${baseId}${adCount}`);
                numberOfAds--;
                adCount++;
            } else {
                newList.push(videoListCopy.shift());
            }
        }

        return newList;
    }

    private getListWithPreRollsAds = () => {
        const { repeatAds, maxAds, firstAdPlace, videoList, showPrerollAds } = this.props;
        const videoListCopy = [...videoList];
        const videoListLength = videoListCopy.length;
        let numberOfAdsLeft = firstAdPlace > videoListLength ? 0 : Math.min(maxAds, videoListLength - firstAdPlace, Math.floor((videoListLength - firstAdPlace) / repeatAds + 1));
        const totalAdsNumber = firstAdPlace > videoListLength ? 0 : Math.min(maxAds, videoListLength - firstAdPlace, Math.floor((videoListLength - firstAdPlace) / repeatAds + 1));

        if (maxAds === 0) return videoListCopy;
        let newList = [];

        for (let i = 0; i < videoList.length; i++) {
            let prerollIndex = Math.abs(numberOfAdsLeft - (totalAdsNumber + 1));
            if (i + 1 < firstAdPlace) {
                newList.push({ ...videoList[i], hidePrerollAds: true, prerollIndex: undefined });
            } else if (i === firstAdPlace && numberOfAdsLeft > 0) {
                newList.push({ ...videoList[i], hidePrerollAds: false, prerollIndex: showPrerollAds ? prerollIndex : undefined });
                numberOfAdsLeft--;
            } else if (numberOfAdsLeft > 0 && (i - firstAdPlace) % (repeatAds + 1) === 0) {
                newList.push({ ...videoList[i], hidePrerollAds: false, prerollIndex: showPrerollAds ? prerollIndex : undefined });
                numberOfAdsLeft--;
            } else {
                newList.push({ ...videoList[i], hidePrerollAds: true, prerollIndex: undefined });
            }
        }

        return newList;
    }

    public render() {
        const { videoList, isTabVisible, isTitleVisible, isSubTitleVisible, tabTitleText, domain, showPrerollAds, youtubeLink, twitterLink,
            whatsappLink, instagramLink, tiktokLink, facebookLink, tabBackgroundColor, isLTR, languageCode, category } = this.props;
        const { isOverlayOpen, selectedVideo, isPromoCarouselInView, currentPromoIndex } = this.state;
        const PromoCarouselComponent = isLTR ? PromoCarouselLtr : PromoCarousel;

        return (
            <div className="videoGalleryVerticalDesktopWrapper" ref={(ref) => { this.videoGalleryRef = ref; }}>
                {isTabVisible &&
                    <div className="videoGalleryVerticalTab componentaTab" style={{ backgroundColor: tabBackgroundColor }}>
                        <span>{tabTitleText}</span>

                        <div className="mediaLinks">
                            {youtubeLink && <a className="youtubeLink" href={youtubeLink} target="blank"></a>}
                            {twitterLink && <a className="twitterLink" href={twitterLink} target="blank"></a>}
                            {whatsappLink && <a className="whatsappLink" href={whatsappLink} target="blank"></a>}
                            {tiktokLink && <a className="tiktokLink" href={tiktokLink} target="blank"></a>}
                            {instagramLink && <a className="instagramLink" href={instagramLink} target="blank"></a>}
                            {facebookLink && <a className="facebookLink" href={facebookLink} target="blank"></a>}
                        </div>
                    </div>
                }

                <PromoCarouselComponent
                    marginRight={15} //when changing make sure to change the carousel slotView css as well to match
                    imageWidth={219}
                    pushGA4Event={this.pushGA4Event}
                    itemListLength={videoList.length}
                >
                    {videoList.map((videoItem, index) =>
                        <NewYnetTvVerticalItem
                            videoIndex={index}
                            videoItem={videoItem}
                            key={videoItem.itemId}
                            onVideoEnded={this.onPromoVideoEnded}
                            currentPromoIndex={currentPromoIndex}
                            onPromoMouseHover={this.onPromoMouseHover}
                            isPromoCarouselInView={isPromoCarouselInView}
                            onOpenVideoGalleryView={this.onOpenVideoGalleryView}
                        />
                    )}
                </PromoCarouselComponent >

                <VerticalVideoOverlayPlayer
                    isLTR={isLTR}
                    domain={domain}
                    isMobile={false}
                    category={category}
                    languageCode={languageCode}
                    isSingleVideoDisplay={false}
                    isOverlayOpen={isOverlayOpen}
                    showPrerollAds={showPrerollAds}
                    isTitleVisible={isTitleVisible}
                    videoItems={this.getListWithAds()}
                    selectedVideoIndex={selectedVideo}
                    isSubTitleVisible={isSubTitleVisible}
                    onVideoOverlayClose={this.onCloseVideoGalleryView}
                />
            </div>
        )
    }
}
